import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Building2, Users, Loader2 } from 'lucide-react';
import { completeSiteOnboarding } from '@/services/inviteApi';

interface CreateOrganizationStepProps {
  onComplete: (data: {
    organization_id: string;
    organization_name: string;
    team_id: string;
    team_name: string;
  }) => void;
}

export function CreateOrganizationStep({ onComplete }: CreateOrganizationStepProps) {
  const [orgName, setOrgName] = useState('');
  const [teamName, setTeamName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!orgName.trim()) {
      setError('Organization name is required');
      return;
    }

    if (!teamName.trim()) {
      setError('Team name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await completeSiteOnboarding({
        organization_name: orgName.trim(),
        team_name: teamName.trim(),
      });
      onComplete(result);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Failed to create organization';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="flex justify-center">
          <Building2 className="h-12 w-12 text-primary" />
        </div>
        <h2 className="text-xl font-semibold">Create Your Organization</h2>
        <p className="text-sm text-muted-foreground">
          Set up your organization and first team to get started.
        </p>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="orgName" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Organization Name
          </Label>
          <Input
            id="orgName"
            placeholder="Acme Inc."
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            disabled={isSubmitting}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            This is your company or organization name.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="teamName" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Name
          </Label>
          <Input
            id="teamName"
            placeholder="Engineering"
            value={teamName}
            onChange={(e) => setTeamName(e.target.value)}
            disabled={isSubmitting}
          />
          <p className="text-xs text-muted-foreground">
            Create your first team. You can add more teams later.
          </p>
        </div>

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating...
            </>
          ) : (
            'Create & Continue'
          )}
        </Button>
      </form>
    </div>
  );
}
